"use client";

import { useSyncExternalStore } from "react";
import type { UploadTilePreview } from "@/lib/upload-tile-preview";

// The gallery renders outside the upload session's React tree, so the
// session publishes what the gallery needs here. Read-only for the gallery:
// nothing flows back into the upload pipeline.

export interface UploadSessionTrigger {
  open: () => void;
  disabled: boolean;
  label: string;
}

export interface UploadSessionBridgeState {
  isDialogOpen: boolean;
  /** A file is pending or uploading (hides «Made with Guest.Gallery»). */
  hasActiveUploads: boolean;
  /** Dialog previews keyed by the uploaded object's file name. */
  localPreviews: ReadonlyMap<string, UploadTilePreview>;
  trigger: UploadSessionTrigger | null;
}

const EMPTY_PREVIEWS: ReadonlyMap<string, UploadTilePreview> = new Map();
const INITIAL_STATE: UploadSessionBridgeState = {
  isDialogOpen: false,
  hasActiveUploads: false,
  localPreviews: EMPTY_PREVIEWS,
  trigger: null,
};

let state = INITIAL_STATE;
const listeners = new Set<() => void>();

export function updateUploadSessionBridge(
  patch: Partial<UploadSessionBridgeState>
): void {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Subscribes to one value of the bridge. The selector must return a primitive
 * or a stable reference, so a component re-renders only when its value
 * changes (a gallery tile re-renders for its own preview, not for every file).
 */
export function useUploadSessionBridge<T>(
  selector: (bridge: UploadSessionBridgeState) => T
): T {
  return useSyncExternalStore(
    subscribe,
    () => selector(state),
    () => selector(INITIAL_STATE)
  );
}

/** The dialog preview for an uploaded object, if this guest just picked it. */
export function useLocalUploadPreview(
  objectFileName: string | null
): UploadTilePreview | undefined {
  return useUploadSessionBridge((bridge) =>
    objectFileName ? bridge.localPreviews.get(objectFileName) : undefined
  );
}

/** File name of an object key or URL, e.g. ".../128/1781970130-uuid.jpg". */
export function getObjectFileName(keyOrUrl: string): string {
  const path = keyOrUrl.split("?")[0];
  return path.slice(path.lastIndexOf("/") + 1);
}

export const EMPTY_LOCAL_PREVIEWS = EMPTY_PREVIEWS;
